// inv/src/components/investor-page/modules/pDealsBlock.jsx
import { useTranslation } from 'react-i18next';
import InvestorLogo from '../../data/InvestorLogo';
import CountryFlag from '../../countryFlags/countryFlag';
import { Badge } from '../../ui/badge';
import { PieChart, BarChart3, Layers, Tags, Info } from 'lucide-react';
import InvestorAccessIcons from '../../investor-display/InvestorAccessIcons';

const formatShortAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;

  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1).replace(/\.0$/, '')}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
  return String(Math.round(n));
};

const DealsCounters = ({ totalDeals, leadCount, participatedCount, bothCount = 0 }) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-md p-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">
            {t('pageInvProf.dealsCount')}
          </span>
          <span className="text-lg font-semibold text-stone-900">
            {totalDeals}
          </span>
        </div>

        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">
            {t('pageInvProf.leadInvTitle')}
          </span>
          <span className="text-lg font-semibold text-stone-900">
            {leadCount}
          </span>
          <span className="text-xs text-slate-600 mt-1">
            {t('pageInvProf.labelBoth2')} {bothCount}
          </span>
        </div>

        <div className="flex flex-col">
          <span className="text-[11px] uppercase tracking-wide text-slate-500">
            {t('pageInvProf.secondInvTitle')}
          </span>
          <span className="text-lg font-semibold text-stone-900">
            {participatedCount}
          </span>
        </div>
      </div>
    </div>
  );
};

const InvestorDealsBlock = ({
  investorData,
  totalDeals,
  leadCount,
  participatedCount,
  bothCount,
  primaryIndustryStats,
  otherTopIndustries = [],
  industryMeta,
}) => {
  const { t, i18n } = useTranslation();

  if (!investorData) return null;

  const totalIndustries = industryMeta?.totalIndustries || 0;
  const topCount = industryMeta?.topCount || 0;

  const headerLine =
    totalIndustries > 0
      ? t('pageInvProf.topIndustriesHeader', {
        name: investorData.name,
        total: totalIndustries,
      })
      : t('pageInvProf.topIndustriesHeaderShort', {
        name: investorData.name,
      });

  const primaryLine =
    primaryIndustryStats &&
    (primaryIndustryStats.dealsSharePct != null &&
      primaryIndustryStats.dealsSharePct > 0
      ? t('pageInvProf.topIndustriesPrimaryLine', {
        sector: primaryIndustryStats.name,
        deals: primaryIndustryStats.deals,
        share: primaryIndustryStats.dealsSharePct,
      })
      : t('pageInvProf.topIndustriesPrimaryLineNoShare', {
        sector: primaryIndustryStats.name,
        deals: primaryIndustryStats.deals,
      }));

  const volumeLine =
    primaryIndustryStats &&
    primaryIndustryStats.totalAmount > 0 &&
    (primaryIndustryStats.avgTicket
      ? t('pageInvProf.topIndustriesVolumeLine', {
        sector: primaryIndustryStats.name,
        amount: formatShortAmount(primaryIndustryStats.totalAmount),
        ticket: formatShortAmount(primaryIndustryStats.avgTicket),
      })
      : t('pageInvProf.topIndustriesVolumeLineNoTicket', {
        sector: primaryIndustryStats.name,
        amount: formatShortAmount(primaryIndustryStats.totalAmount),
      }));

  const othersList =
    otherTopIndustries && otherTopIndustries.length
      ? otherTopIndustries.join(', ')
      : '';

  const othersLine =
    othersList &&
    t('pageInvProf.topIndustriesOthersLine', {
      list: othersList,
    });

  const topCountNote =
    topCount > 0
      ? t('pageInvProf.topIndustriesTopCountNote', { count: topCount })
      : null;

  return (
    <div className="w-full mb-6 bg-gradient-to-r from-slate-50 to-gray-100 p-6 rounded-md shadow-md">
      <div className="mb-6">
        <a
          href={i18n.language === 'en' ? '/find-investors' : `/${i18n.language}/find-investors`}
          className="text-secondary hover:underline mr-4"
        >
          ← {t('pageInvProf.backtoBtn')}
        </a>
        <h2 className="mt-4 text-lg lg:text-xl font-normal flex items-center">
          {t('pageInvProf.h2')}:
          <span className="ml-2 flex items-center space-x-2 font-bold">
            <InvestorLogo
              id={investorData.id}
              country={investorData.country}
              className="w-6 h-6"
              alt={`${investorData.name} logo`}
            />
            <span>{investorData.name}</span>
          </span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-lg">
        <div className="flex flex-col gap-4 bg-white p-4 rounded-lg shadow">
          <div className="mt-4">
            <InvestorAccessIcons investor={investorData} socialMode="hint" />
          </div>

          {investorData.location && (
            <div className="flex flex-col text-sm">
              <strong>{t('pageFindInvestors.invAddress')}:</strong>
              <span>{investorData.location}</span>
            </div>
          )}

          {investorData.country && (
            <div className="flex flex-col text-sm">
              <strong>{t('pageFindInvestors.invCountry')}:</strong>
              <CountryFlag countryName={investorData.country} />
            </div>
          )}
        </div>

        {(investorData.industries?.length > 0 || primaryIndustryStats) && (
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm">
              {(primaryIndustryStats || totalIndustries > 0) && (
                <h3 className="mt-0 mb-2 flex items-center gap-2">
                  <PieChart className="w-4 h-4" aria-hidden="true" />
                  <span>{headerLine}</span>
                </h3>
              )}

              {primaryLine && (
                <p className="mt-2 mb-1 flex items-start gap-2">
                  <BarChart3 className="w-4 h-4 mt-[2px]" aria-hidden="true" />
                  <span>{primaryLine}</span>
                </p>
              )}

              {volumeLine && (
                <p className="mt-0 mb-1 flex items-start gap-2">
                  <BarChart3 className="w-4 h-4 mt-[2px]" aria-hidden="true" />
                  <span>{volumeLine}</span>
                </p>
              )}

              {othersLine && (
                <p className="mt-0 mb-1 flex items-start gap-2">
                  <Layers className="w-4 h-4 mt-[2px]" aria-hidden="true" />
                  <span>{othersLine}</span>
                </p>
              )}

              {(topCountNote || true) && (
                <p className="mt-2 mb-2 text-xs flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 mt-[2px]" aria-hidden="true" />
                  <span>
                    {topCountNote && <span>{topCountNote} · </span>}
                    {t(
                      'pageInvProf.topIndustriesCtaShort',
                      'Full sector breakdown is available in the dashboard for Startup and Professional plans.'
                    )}
                  </span>
                </p>
              )}

              {investorData.industries?.length > 0 && (
                <>
                  <p className="mt-3 mb-3 font-medium flex items-center gap-2">
                    <Tags className="w-4 h-4" aria-hidden="true" />
                    <span>{t('pageFindInvestors.invIndustry')}:</span>
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {investorData.industries.map((industry, i) => (
                      <Badge
                        key={i}
                        variant="v1"
                        text={industry.trim()}
                        as="a"
                        href={`${i18n.language === 'en' ? '' : `/${i18n.language}`
                          }/find-investors?industries=${encodeURIComponent(industry.trim())}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {typeof investorData.short_info === 'string' &&
          investorData.short_info.trim() && (
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm">{investorData.short_info}</div>
            </div>
          )}

        <div className="bg-white p-4 rounded-lg shadow">
          <DealsCounters
            totalDeals={totalDeals}
            leadCount={leadCount}
            participatedCount={participatedCount}
            bothCount={bothCount}
          />
        </div>
      </div>
    </div>
  );
};

export default InvestorDealsBlock;
